import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Bse2Badge } from "@/components/Bse2Badge";
import { formatCurrency, formatDate } from "@/lib/format";
import { Plus, ListOrdered, Pause, Play, ArrowRightLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const PLAN_META = {
  sip: { regField: "sip_reg_id", installmentsField: "no_of_installments", label: "SIP" },
  xsip: { regField: "xsip_reg_id", installmentsField: "no_of_installments", label: "XSIP" },
  stp: { regField: "stp_reg_no", installmentsField: "no_of_transfers", label: "STP" },
  swp: { regField: "swp_reg_no", installmentsField: "number_of_withdrawals", label: "SWP" },
};

function TopupDialog({ open, kind, regId, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const start = new Date();
      start.setMonth(start.getMonth() + 1);
      const dd = String(start.getDate()).padStart(2, "0");
      const mm = String(start.getMonth() + 1).padStart(2, "0");
      const { data } = await clientApi.post(`/systematic/${kind}/${regId}/topup`, {
        amount: Number(amount), start_date: `${dd}/${mm}/${start.getFullYear()}`,
      });
      if (data.status === "BSE_RECONCILIATION_REQUIRED") toast.warning(data.bse2?.message || "BSE top-up outcome is pending; do not repeat it.");
      else toast.success(data.bse2?.message || `Top-up of ₹${amount} submitted to BSE`);
      setAmount("");
      onDone();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading text-[var(--brand)]">Top-up {kind ? PLAN_META[kind].label : ""} #{regId}</DialogTitle>
          <DialogDescription>Adds an extra recurring amount on top of your existing installments (annual step-up, per BSE rules).</DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <Label>Additional monthly amount (INR)</Label>
            <Input type="number" min="100" required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="topup-amount-input" />
          </div>
          <Button type="submit" disabled={saving} className="w-full rounded-full bg-[var(--cta)]" data-testid="topup-submit-button">
            {saving ? "Registering..." : "Register top-up"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InstallmentsDialog({ open, onClose, planType, regNo }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !regNo) return;
    setLoading(true);
    clientApi.get(`/installments/${planType}/${regNo}`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [open, planType, regNo]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="installments-dialog">
        <DialogHeader><DialogTitle className="font-heading text-[var(--brand)]">Installments &middot; Reg# {regNo}</DialogTitle></DialogHeader>
        {loading ? <div className="p-4 text-sm text-[var(--ink-muted)]">Loading...</div> :
          rows.length === 0 ? <div className="p-4 text-sm text-[var(--ink-muted)]" data-testid="installments-empty">BSE has not reported any installments for this plan yet.</div> :
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {rows.map((r) => (
              <div key={r.id} className="flex flex-col gap-2 rounded-lg border border-[var(--glass-border)] p-3 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid={`installment-row-${r.installment_no}`}>
                <div className="min-w-0">
                  <div className="font-medium">Installment #{r.installment_no}</div>
                  <div className="text-xs text-[var(--ink-muted)]">Order #{r.order_number} &middot; {formatDate(r.created_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{formatCurrency(r.amount)}</span>
                  <StatusBadge status={r.status} />
                </div>
              </div>
            ))}
          </div>}
      </DialogContent>
    </Dialog>
  );
}

function PauseDialog({ open, onClose, kind, regId, onDone }) {
  const [months, setMonths] = useState("1");
  const [loading, setLoading] = useState(false);

  const handlePause = async () => {
    setLoading(true);
    try {
      const { data } = await clientApi.post(`/systematic/${kind}/${regId}/pause`, { pause_months: Number(months) });
      if (data.status === "PAUSE_RECONCILIATION_REQUIRED") toast.warning(data.bse2?.message || "BSE pause outcome is pending; do not repeat it.");
      else toast.success(data.bse2?.message || "Pause request submitted to BSE");
      onDone();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pause-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[var(--brand)]">Pause {PLAN_META[kind]?.label}</DialogTitle>
          <DialogDescription>BSE allows pausing installments for up to 3 months. It will auto-resume after the selected period.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Pause duration</Label>
            <Select value={months} onValueChange={setMonths}>
              <SelectTrigger data-testid="pause-months-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 month</SelectItem>
                <SelectItem value="2">2 months</SelectItem>
                <SelectItem value="3">3 months (max)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button disabled={loading} onClick={handlePause} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="pause-confirm-button">
            {loading ? "Pausing..." : "Confirm Pause"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShiftToXsipDialog({ open, onClose, regId, onDone }) {
  const [mandates, setMandates] = useState([]);
  const [mandateId, setMandateId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    clientApi.get("/mandates").then((r) => {
      const approved = r.data.filter((m) => m.status === "APPROVED");
      setMandates(approved);
      if (approved.length) setMandateId(approved[0].mandate_id);
    });
  }, [open]);

  const handleShift = async () => {
    setLoading(true);
    try {
      const { data } = await clientApi.post(`/sips/${regId}/shift-to-xsip`, { mandate_id: mandateId });
      toast.success(data.message);
      onDone();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="shift-to-xsip-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[var(--brand)]">Link mandate to SIP</DialogTitle>
          <DialogDescription>Changes the BSE mandate linked to this existing SIP. The SIP registration itself is not recreated.</DialogDescription>
        </DialogHeader>
        {mandates.length === 0 ? (
          <div className="text-sm text-[var(--ink-muted)]" data-testid="shift-no-mandates-message">You need an approved mandate first. Register one from the Mandates page.</div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Select mandate</Label>
              <Select value={mandateId} onValueChange={setMandateId}>
                <SelectTrigger data-testid="shift-mandate-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {mandates.map((m) => <SelectItem key={m.mandate_id} value={m.mandate_id}>#{m.mandate_id} &middot; {formatCurrency(m.amount)} limit</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button disabled={loading} onClick={handleShift} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="shift-confirm-button">
              {loading ? "Linking at BSE..." : "Link mandate"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PlanRow({ item, kind, onCancel, onViewInstallments, onPause, onResume, onShift, onTopup, onRefresh, refreshing }) {
  const meta = PLAN_META[kind];
  const regId = item[meta.regField];
  const schemeLabel = item.scheme_name || `${item.from_scheme_name} \u2192 ${item.to_scheme_name}`;
  const canPause = (kind === "sip" || kind === "xsip") && item.status === "ACTIVE";
  const canResume = (kind === "sip" || kind === "xsip") && item.status === "PAUSED";
  const canShift = kind === "sip" && (item.status === "ACTIVE" || item.status === "PAUSED");
  return (
    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`${kind}-row-${regId}`}>
      <div>
        <div className="font-medium text-[var(--ink)]">{schemeLabel}</div>
        <div className="text-xs text-[var(--ink-muted)]">
          {item.frequency_type} &middot; {formatCurrency(item.installment_amount)} &middot; Started {formatDate(item.start_date)} &middot; Reg# {regId} &middot; {item.installments_done || 0}/{item[meta.installmentsField]} done
          {item.status === "PAUSED" && item.paused_until && <> &middot; Resumes {item.paused_until}</>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={item.status} />
        <Bse2Badge bse2={item.bse2} />
        {item.bse2?.reg_no && <Button size="sm" variant="outline" className="rounded-full" disabled={refreshing} onClick={() => onRefresh(kind, regId)}>
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />BSE status
        </Button>}
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onViewInstallments(kind, regId)} data-testid={`${kind}-view-installments-${regId}`}>
          <ListOrdered className="mr-1 h-3.5 w-3.5" />Installments
        </Button>
        {canPause && (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onPause(kind, regId)} data-testid={`${kind}-pause-${regId}`}>
            <Pause className="mr-1 h-3.5 w-3.5" />Pause
          </Button>
        )}
        {canResume && (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onResume(kind, regId)} data-testid={`${kind}-resume-${regId}`}>
            <Play className="mr-1 h-3.5 w-3.5" />Resume
          </Button>
        )}
        {canShift && (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onShift(regId)} data-testid={`${kind}-shift-${regId}`}>
            <ArrowRightLeft className="mr-1 h-3.5 w-3.5" />Link mandate
          </Button>
        )}
        {item.status === "ACTIVE" && (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onTopup(kind, regId)} data-testid={`${kind}-topup-${regId}`}>
            <Plus className="mr-1 h-3.5 w-3.5" />Top-up
          </Button>
        )}
        {item.status === "ACTIVE" && (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onCancel(kind, regId)} data-testid={`${kind}-cancel-${regId}`}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

export default function SipCenter() {
  const [sips, setSips] = useState([]);
  const [xsips, setXsips] = useState([]);
  const [stps, setStps] = useState([]);
  const [swps, setSwps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, planType: null, regNo: null });
  const [pauseDialog, setPauseDialog] = useState({ open: false, kind: null, regId: null });
  const [topupDialog, setTopupDialog] = useState({ open: false, kind: null, regId: null });
  const [shiftDialog, setShiftDialog] = useState({ open: false, regId: null });
  const [refreshing, setRefreshing] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      clientApi.get("/sips"), clientApi.get("/xsips"), clientApi.get("/stp"), clientApi.get("/swp"),
    ]).then(([s, x, st, sw]) => { setSips(s.data); setXsips(x.data); setStps(st.data); setSwps(sw.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const cancelHandlers = {
    sip: (regId) => clientApi.post(`/sips/${regId}/cancel`),
    xsip: (regId) => clientApi.post(`/xsips/${regId}/cancel`),
    stp: (regId) => clientApi.post(`/stp/${regId}/cancel`),
    swp: (regId) => clientApi.post(`/swp/${regId}/cancel`),
  };

  const handleCancel = async (kind, regId) => {
    try {
      const { data } = await cancelHandlers[kind](regId);
      if (data.status === "CANCEL_RECONCILIATION_REQUIRED") toast.warning(data.bse2?.message || "BSE cancellation outcome is pending; do not repeat it.");
      else toast.success(data.bse2?.message || `${PLAN_META[kind].label} cancellation submitted to BSE`);
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const handleResume = async (kind, regId) => {
    try {
      const { data } = await clientApi.post(`/systematic/${kind}/${regId}/resume`);
      if (data.status === "RESUME_RECONCILIATION_REQUIRED") toast.warning(data.bse2?.message || "BSE resume outcome is pending; do not repeat it.");
      else toast.success(data.bse2?.message || "Resume request submitted to BSE");
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const handleRefresh = async (kind, regId) => {
    const key = `${kind}-${regId}`;
    setRefreshing(key);
    try {
      const { data } = await clientApi.post(`/systematic/${kind}/${regId}/bse-refresh`);
      toast.success(`BSE ${PLAN_META[kind].label} status: ${data.live_status || "refreshed"}`);
      await load();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
    finally { setRefreshing(""); }
  };

  return (
    <ClientLayout>
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]">Systematic Plans</h1>
          <p className="mt-1 text-sm text-[var(--ink-muted)]">Manage your SIP, XSIP, STP and SWP registrations.</p>
        </div>
        <Button asChild className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="new-sip-button">
          <Link to="/sips/new"><Plus className="mr-2 h-4 w-4" /> New Plan</Link>
        </Button>
      </div>

      <Tabs defaultValue="sip">
        <TabsList>
          <TabsTrigger value="sip" data-testid="tab-sip">SIP ({sips.length})</TabsTrigger>
          <TabsTrigger value="xsip" data-testid="tab-xsip">XSIP ({xsips.length})</TabsTrigger>
          <TabsTrigger value="stp" data-testid="tab-stp">STP ({stps.length})</TabsTrigger>
          <TabsTrigger value="swp" data-testid="tab-swp">SWP ({swps.length})</TabsTrigger>
        </TabsList>
        {[["sip", sips, "No SIPs yet."], ["xsip", xsips, "No XSIPs yet. XSIPs require a registered mandate."],
          ["stp", stps, "No STPs yet."], ["swp", swps, "No SWPs yet."]].map(([kind, items, emptyMsg]) => (
          <TabsContent key={kind} value={kind}>
            <Card className="border-[var(--glass-border)]"><CardContent className="p-0">
              {loading ? <div className="p-6 text-sm text-[var(--ink-muted)]">Loading...</div> :
                items.length === 0 ? <div className="p-6 text-sm text-[var(--ink-muted)]" data-testid={`no-${kind}s-message`}>{emptyMsg}</div> :
                <div className="divide-y divide-[var(--glass-border)]">
                  {items.map((item) => (
                    <PlanRow
                      key={item[PLAN_META[kind].regField]}
                      item={item} kind={kind}
                      onCancel={handleCancel}
                      onViewInstallments={(k, regNo) => setDialog({ open: true, planType: k, regNo })}
                      onPause={(k, regId) => setPauseDialog({ open: true, kind: k, regId })}
                      onResume={handleResume}
                      onShift={(regId) => setShiftDialog({ open: true, regId })}
                      onTopup={(k, regId) => setTopupDialog({ open: true, kind: k, regId })}
                      onRefresh={handleRefresh}
                      refreshing={refreshing === `${kind}-${item[PLAN_META[kind].regField]}`}
                    />
                  ))}
                </div>}
            </CardContent></Card>
          </TabsContent>
        ))}
      </Tabs>

      <InstallmentsDialog
        open={dialog.open} planType={dialog.planType} regNo={dialog.regNo}
        onClose={() => setDialog({ open: false, planType: null, regNo: null })}
      />
      <PauseDialog
        open={pauseDialog.open} kind={pauseDialog.kind} regId={pauseDialog.regId}
        onClose={() => setPauseDialog({ open: false, kind: null, regId: null })}
        onDone={() => { setPauseDialog({ open: false, kind: null, regId: null }); load(); }}
      />
      {topupDialog.open && (
        <TopupDialog
          open={topupDialog.open} kind={topupDialog.kind} regId={topupDialog.regId}
          onClose={() => setTopupDialog({ open: false, kind: null, regId: null })}
          onDone={() => { setTopupDialog({ open: false, kind: null, regId: null }); load(); }}
        />
      )}
      <ShiftToXsipDialog
        open={shiftDialog.open} regId={shiftDialog.regId}
        onClose={() => setShiftDialog({ open: false, regId: null })}
        onDone={() => { setShiftDialog({ open: false, regId: null }); load(); }}
      />
    </ClientLayout>
  );
}
